import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { Box, CardContent, CardHeader, Tab } from "@mui/material";
import { MprcBarTabs, MprcCard, MprcIconFa } from "components";
import { MprcContainer } from "components/MprcContainer";
import { UserEditProfileResponse, UserPhoneTypeSelectorResponse } from "generated/CoreApi_v1";
import { TFunction } from "i18next";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditUserAdditionalInfoBox } from "views/foundation/user/Home/EditProfile/EditUserAdditionalInfoBox";
import { EditUserInterestBox } from "views/foundation/user/Home/EditProfile/EditUserInterestBox";
import { EditUserPersonInfoBox } from "views/foundation/user/Home/EditProfile/EditUserPersonInfoBox";
import { EditUserPreferencesBox } from "views/foundation/user/Home/EditProfile/EditUserPreferencesBox";

////////////////////////////////////////////////////////////////////////////

export type UserTabOptions = "interest" | "personalInfo" | "preferences" | "additionalInfo";

interface IProps {
    viewData: UserEditProfileResponse;
    viewErrors: ApiProblemErrors | undefined;
    phoneTypeItems: UserPhoneTypeSelectorResponse[];
    onFormUpdate: (dto: UserEditProfileResponse) => void;
    onFormValidation: (valid: boolean) => void;
    onSubForm: (subform: any) => void;
    onError: (e: any) => void;
}

interface IBoxValidations {
    personalInfoBox?: boolean;
    additionalInfoBox?: boolean;
}

////////////////////////////////////////////////////////////////////////////

///
function getEmptyValidations(): IBoxValidations {
    return { personalInfoBox: undefined, additionalInfoBox: undefined };
}

///
function getTabIcon(show: boolean, t: TFunction) {
    return show === true ? (
        <MprcIconFa
            icon={faTriangleExclamation}
            color="warning"
            fixedWidth
            title={t("User.Edit.AlertInvalidValuesTooltip")}
        />
    ) : (
        <div>{/* Deixa em branco, para manter o mesmo height de quando o ícone é exibido */}</div>
    );
}

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Usuário - Formulário secundário
 * @param props
 */
export const EditUserSecondaryBox: React.FC<IProps> = (props) => {
    const { viewData, viewErrors, phoneTypeItems, onFormUpdate, onFormValidation, onSubForm } = props;

    const { t } = useTranslation();

    const [currentTab, setTab] = useState<UserTabOptions>("interest");
    const [boxValidations, setBoxValidations] = useState<IBoxValidations>(getEmptyValidations());

    ////////////////////////////////////////////////////////////////////////////
    useEffect(() => {
        onFormValidation(Object.values(boxValidations).filter((x) => x === false || x === undefined).length == 0);
    }, [boxValidations]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <MprcContainer>
            <MprcCard>
                <CardHeader
                    title={
                        <MprcBarTabs value={currentTab} onChange={(v, selected) => setTab(selected)} variant="fullWidth">
                            <Tab
                                value="interest"
                                label={t("User.Edit.InterestTitle")}
                                iconPosition="end"
                            />
                            <Tab
                                value="personalInfo"
                                label={t("User.Edit.PersonalTitle")}
                                icon={getTabIcon(boxValidations.personalInfoBox === false && boxValidations.personalInfoBox != undefined, t)}
                                iconPosition="end"
                            />
                            <Tab
                                value="preferences"
                                label={t("User.Edit.PreferencesTitle")}
                            />
                            {(
                                ((viewData.customFieldsAdditional ?? []).length > 0 || (viewData.customFieldsConcealed ?? []).length > 0)
                                || viewData.permissions.allowAddRecordFiles
                                || viewData.permissions.allowViewRecordFiles
                            ) &&
                                <Tab
                                    value="additionalInfo"
                                    label={t("User.Edit.OthersTitle")}
                                    icon={getTabIcon(boxValidations.additionalInfoBox === false && boxValidations.additionalInfoBox != undefined, t)}
                                    iconPosition="end"
                                />
                            }
                        </MprcBarTabs>
                    }
                />

                <CardContent>
                    <Box sx={currentTab === "interest" ? { display: "block" } : { display: "none" }}>
                        <EditUserInterestBox
                            viewData={viewData}
                            onSubForm={onSubForm}
                        />
                    </Box>
                    <Box sx={currentTab === "personalInfo" ? { display: "block" } : { display: "none" }}>
                        <EditUserPersonInfoBox
                            onFormUpdate={onFormUpdate}
                            onFormValidation={(valid) =>
                                setBoxValidations((state) => {
                                    return { ...state, personalInfoBox: valid };
                                })
                            }
                            viewData={viewData}
                            viewErrors={viewErrors}
                            phoneTypeItems={phoneTypeItems}
                        />
                    </Box>
                    <Box sx={currentTab === "preferences" ? { display: "block" } : { display: "none" }}>
                        <EditUserPreferencesBox
                            onFormUpdate={onFormUpdate}
                            viewData={viewData}
                            viewErrors={viewErrors}
                            phoneTypeItems={phoneTypeItems}
                        />
                    </Box>
                    <Box sx={currentTab === "additionalInfo" ? { display: "block" } : { display: "none" }}>
                        <EditUserAdditionalInfoBox
                            onFormUpdate={onFormUpdate}
                            onFormValidation={(valid) =>
                                setBoxValidations((state) => {
                                    return { ...state, additionalInfoBox: valid };
                                })
                            }
                            viewData={viewData}
                            viewErrors={viewErrors}
                            onSubForm={onSubForm}
                        />
                    </Box>
                </CardContent>
            </MprcCard>
        </MprcContainer>
    );
};
